import { useMemo, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useActiveOrganization } from '@/entities/organization';
import { useAuth } from '@/features/auth';
import { OrganizationMembershipResponse, useGetMyAccount } from '@/shared/api';
import { RouterContext, PageRoutes } from '@/shared/router';
import { themeConfig, useThemeSetup } from '@/shared/theme';
import { StorageKeys } from '@/shared/types';
import { useFullScreenLoaderEffect } from '@/shared/ui';
import { captureError } from '@/shared/utils';
import { useRolesAccessVerification } from '../hooks';

type Props = {
  element: ReactNode;
  allowedRoles?: Array<OrganizationMembershipResponse['role']>;
};

export const PrivateRoute = ({ allowedRoles, element }: Props) => {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const { accessToken } = useAuth();
  const theme = useThemeSetup(isAuthenticated);

  const [activeOrganization, setActiveOrganization] = useActiveOrganization();
  const [dashboardGoBackLink, setDashboardGoBackLink] = useState('/dashboard');
  const isDataInitialised = accessToken && activeOrganization?.name;
  const { hasRoleAccess, roleErrorRedirectPath } = useRolesAccessVerification(
    activeOrganization,
    allowedRoles,
  );

  const auth0Error = useMemo(() => JSON.parse(error?.message || '{}'), [error]);

  useFullScreenLoaderEffect(!isDataInitialised);
  useGetMyAccount(
    {},
    {
      select: (data) => data.organizations,
      enabled: Boolean(accessToken),
      onSuccess(data) {
        const newOrganization =
          data.find(({ id }) => id === activeOrganization?.id) ?? data[0];
        setActiveOrganization(newOrganization);
      },
      onError: (error) =>
        captureError(error, { module: 'auth', action: 'get_my_account' }),
    },
  );

  if (isLoading || (isAuthenticated && !isDataInitialised)) {
    return null;
  }

  if (auth0Error?.code === 'user_not_verified') {
    return (
      <Navigate to={PageRoutes.AUTHORISATION_ERROR} state={{ error }} replace />
    );
  }

  // case when user clicks invitation link from email and is not logged in
  if (
    !isAuthenticated &&
    location.pathname.includes(PageRoutes.INVITATION + '/')
  ) {
    localStorage.setItem(StorageKeys.CallbackUrl, location.pathname);
    return <Navigate to={PageRoutes.LOGIN} replace />;
  }
  if (!isAuthenticated) {
    return <Navigate to={PageRoutes.HOME} replace />;
  }
  if (!hasRoleAccess) {
    return <Navigate to={roleErrorRedirectPath} replace />;
  }

  if (isDataInitialised) {
    return (
      <Wrapper
        $scrollbarThumbColor={themeConfig[theme].colors.contentBgSecondary}
        style={{ backgroundColor: themeConfig[theme].colors.bgBase }}
      >
        <RouterContext.Provider
          value={{
            activeOrganization,
            setActiveOrganization,
            dashboardGoBackLink,
            setDashboardGoBackLink,
          }}
        >
          {element}
        </RouterContext.Provider>
      </Wrapper>
    );
  }
  return <Navigate to={PageRoutes.HOME} state={{ from: location }} />;
};

const Wrapper = styled.div<{ $scrollbarThumbColor: string }>`
  *::-webkit-scrollbar {
    width: 14px;
  }
  *::-webkit-scrollbar-thumb {
    background-color: ${({ $scrollbarThumbColor }) =>
      $scrollbarThumbColor} !important;
    border: 4px solid transparent;
    border-radius: 8px;
    background-clip: padding-box;
  }
  *::-webkit-scrollbar {
    width: 14px;
  }
  *::-webkit-scrollbar-thumb {
    background-color: ${({ $scrollbarThumbColor }) =>
      $scrollbarThumbColor} !important;
    border: 4px solid transparent;
    border-radius: 8px;
    background-clip: padding-box;
  }
`;
